<script setup lang="ts">
import { IConfirmDialogProps } from '@/composables/useConfirmDialog';
import CustomButton from '@/core/elements/CustomButton.vue';
import { EIcon } from 'common/enum/misc.enum';
import { computed, useTemplateRef } from 'vue';
import AnyIcon from '../elements/AnyIcon.vue';
import IconButton from '../elements/IconButton.vue';
import FormTextInputPreventAutofill from '../forms/FormTextInputPreventAutofill.vue';

const props = withDefaults(
  defineProps<{
    isMobile?: boolean;
    options: IConfirmDialogProps | null;
  }>(),
  {
    isMobile: false,
  },
);

const confirmInputRef = useTemplateRef<InstanceType<typeof FormTextInputPreventAutofill>>('confirmInputRef');

const emit = defineEmits<{
  close: [];
  confirm: [];
  decline: [];
}>();

const theme = computed(() => (props.options?.theme === 'danger' ? 'error' : 'primary'));
const size = computed(() => props.options?.size ?? 'medium');
const descriptionComponent = computed(() =>
  typeof props.options?.description === 'function' ? props.options.description : null,
);
const isButtonDisabled = computed(() => {
  if (!props.options?.checkInput) return false;

  const currentValue = confirmInputRef.value?.getValue();
  if (currentValue === undefined) return true;
  const expected = props.options.checkInput.inputCheckValue;
  const validValues = Array.isArray(expected) ? expected : [expected];
  return !validValues.includes(currentValue);
});

const resetInput = (): void => {
  confirmInputRef.value?.resetField();
};

defineExpose({
  resetInput,
});
</script>

<template>
  <header :class="isMobile ? 'flex shrink-0 items-center justify-between gap-md' : 'flex items-center justify-between'">
    <h1 :class="isMobile ? 'min-w-0 text-heading-semibold' : 'text-title font-medium tracking-[0.23px]'">
      {{ options?.title }}
    </h1>
    <IconButton
      v-if="!options?.hideCloseButton"
      type="button"
      size="button"
      :icon-name="EIcon.X"
      theme="text"
      :class="isMobile && 'shrink-0'"
      @click="emit('close')"
    />
  </header>

  <!-- Descriptions include user-controlled names. Never parse strings as HTML, even after translation. -->
  <div
    :class="[
      'overflow-y-auto pt-lg leading-[170%] whitespace-pre-wrap [&_strong]:font-semibold break-words hyphens-auto',
      isMobile && 'max-h-[46dvh] min-h-0',
    ]"
  >
    <component :is="descriptionComponent" v-if="descriptionComponent" />
    <template v-else>{{ options?.description }}</template>
  </div>

  <!-- Own form so an autofill cannot reach the page fields behind the dialog. -->
  <form v-if="options?.checkInput" class="pt-lg" @submit.prevent>
    <FormTextInputPreventAutofill
      ref="confirmInputRef"
      name="confirmInput"
      :placeholder="options?.checkInput?.inputPlaceholder"
    />
  </form>

  <div v-if="options?.hint" class="flex justify-start gap-sm overflow-y-auto pt-lg text-secondary">
    <AnyIcon :icon-name="EIcon.LIGHT_BULB" class="mb-sm size-icon-md text-tint" />
    {{ options?.hint }}
  </div>

  <footer
    :class="
      isMobile
        ? 'flex shrink-0 flex-col items-stretch gap-sm pt-xl'
        : [
            'flex flex-wrap items-end gap-sm pt-xl',
            size === 'lg' && options?.confirmButtonLabel ? 'justify-between' : 'justify-end',
            options?.buttonAlignment === 'between'
              ? 'justify-between'
              : options?.buttonAlignment === 'end'
                ? 'justify-end'
                : '',
          ]
    "
  >
    <CustomButton
      :class="[isMobile ? 'w-full' : 'w-full sm:w-auto', 'justify-center']"
      :theme="options?.confirmButtonLabel ? 'secondary' : 'primary'"
      @click="emit('decline')"
    >
      {{ options?.declineButtonLabel }}
    </CustomButton>
    <CustomButton
      v-if="options?.confirmButtonLabel"
      :class="[isMobile ? 'w-full whitespace-nowrap !px-smd' : 'w-full sm:w-auto', 'justify-center']"
      :theme="theme"
      :disabled="isButtonDisabled"
      @click="emit('confirm')"
    >
      <AnyIcon
        v-if="options?.confirmButtonIcon && !isMobile"
        :icon-name="options.confirmButtonIcon"
        class="size-icon-md shrink-0"
      />
      {{ options?.confirmButtonLabel }}
    </CustomButton>
  </footer>
</template>
